/**
 * offlineQueue — durable browser-backed write-ahead log for offline mutations.
 *
 * Each entry represents one HTTP mutation the user issued while offline. The
 * sync worker drains the queue when the network returns. Idempotency keys are
 * generated client-side BEFORE enqueue so the server can collapse retries.
 */
import { del, get, set } from "idb-keyval";
import { buildIdempotencyKey } from "./idempotencyKey";

export { deterministicKey } from "./idempotencyKey";

export type OfflineMethod = "POST" | "PUT" | "PATCH" | "DELETE";
export type OfflineEntryStatus = "pending" | "in_flight" | "failed";

export interface OfflineEntry {
  id: string;
  createdAt: string;
  endpoint: string;
  method: OfflineMethod;
  body: unknown;
  idempotencyKey: string;
  status: OfflineEntryStatus;
  attempts: number;
  lastError?: string;
}

const STORE_KEY = "phd:offline-queue:v1";
const LEGACY_LOCAL_STORAGE_KEY = STORE_KEY;
export const QUEUE_MAX_SIZE = 500;

const listeners = new Set<(entries: OfflineEntry[]) => void>();
let memoryQueue: OfflineEntry[] = [];

function emit(entries: OfflineEntry[]) {
  for (const l of listeners) l(entries);
}

function isOfflineEntryArray(value: unknown): value is OfflineEntry[] {
  return Array.isArray(value);
}

function readLegacyLocalStorage(): OfflineEntry[] | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(LEGACY_LOCAL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isOfflineEntryArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function clearLegacyLocalStorage(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY);
  } catch {
    // Ignore storage failures; IndexedDB remains the source of truth.
  }
}

async function readAll(): Promise<OfflineEntry[]> {
  if (typeof indexedDB === "undefined") {
    return [...memoryQueue];
  }

  try {
    const stored = await get<OfflineEntry[]>(STORE_KEY);
    if (isOfflineEntryArray(stored)) return stored;

    const legacy = readLegacyLocalStorage();
    if (legacy) {
      await set(STORE_KEY, legacy);
      clearLegacyLocalStorage();
      return legacy;
    }

    return [];
  } catch {
    return [...memoryQueue];
  }
}

async function writeAll(entries: OfflineEntry[]): Promise<void> {
  if (typeof indexedDB === "undefined") {
    memoryQueue = [...entries];
  } else {
    try {
      if (entries.length === 0) {
        await del(STORE_KEY);
      } else {
        await set(STORE_KEY, entries);
      }
      clearLegacyLocalStorage();
    } catch {
      memoryQueue = [...entries];
    }
  }
  emit(entries);
}

export interface EnqueueInput {
  endpoint: string;
  method: OfflineMethod;
  body: unknown;
  idempotencyKey: string;
}

export async function enqueue(input: EnqueueInput): Promise<OfflineEntry> {
  const all = await readAll();
  const entryId = await buildIdempotencyKey(
    "offline-entry",
    input.method,
    input.endpoint,
    input.idempotencyKey,
  );
  const existingIndex = all.findIndex((entry) => entry.id === entryId);

  if (existingIndex !== -1) {
    const existing = all[existingIndex];
    if (!existing) {
      throw new Error("Offline queue entry lookup failed");
    }
    if (existing.status === "failed") {
      const revived: OfflineEntry = {
        ...existing,
        body: input.body,
        status: "pending",
        lastError: undefined,
      };
      all[existingIndex] = revived;
      await writeAll(all);
      return revived;
    }
    return existing;
  }

  // Cap at QUEUE_MAX_SIZE; drop oldest non-failed entries first.
  if (all.length >= QUEUE_MAX_SIZE) {
    const droppable = [...all]
      .map((entry, idx) => ({ entry, idx }))
      .filter(({ entry }) => entry.status !== "failed")
      .sort(
        (a, b) =>
          new Date(a.entry.createdAt).getTime() -
          new Date(b.entry.createdAt).getTime(),
      );
    const toDrop = droppable.slice(0, all.length - QUEUE_MAX_SIZE + 1);
    const dropIds = new Set(toDrop.map(({ entry }) => entry.id));
    for (let i = all.length - 1; i >= 0; i--) {
      const entry = all[i];
      if (entry && dropIds.has(entry.id)) all.splice(i, 1);
    }
  }

  const entry: OfflineEntry = {
    id: entryId,
    createdAt: new Date().toISOString(),
    endpoint: input.endpoint,
    method: input.method,
    body: input.body,
    idempotencyKey: input.idempotencyKey,
    status: "pending",
    attempts: 0,
  };
  all.push(entry);
  await writeAll(all);
  return entry;
}

export async function peekAll(): Promise<OfflineEntry[]> {
  return readAll();
}

export async function dequeue(id: string): Promise<void> {
  const all = await readAll();
  const next = all.filter((e) => e.id !== id);
  if (next.length !== all.length) await writeAll(next);
}

export async function markInFlight(id: string): Promise<void> {
  const all = await readAll();
  const idx = all.findIndex((e) => e.id === id);
  if (idx === -1) return;
  const current = all[idx];
  if (!current) return;
  all[idx] = { ...current, status: "in_flight", attempts: current.attempts + 1 };
  await writeAll(all);
}

export async function markFailed(id: string, error: string): Promise<void> {
  const all = await readAll();
  const idx = all.findIndex((e) => e.id === id);
  if (idx === -1) return;
  const current = all[idx];
  if (!current) return;
  all[idx] = { ...current, status: "failed", lastError: error };
  await writeAll(all);
}

export async function markPending(id: string): Promise<void> {
  const all = await readAll();
  const idx = all.findIndex((e) => e.id === id);
  if (idx === -1) return;
  const current = all[idx];
  if (!current) return;
  all[idx] = { ...current, status: "pending" };
  await writeAll(all);
}

export async function clearAll(): Promise<void> {
  await writeAll([]);
}

export function subscribe(
  listener: (entries: OfflineEntry[]) => void,
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
